'use client';

import { useState } from 'react';

import Link from 'next/link';

import { EMAIL_VALIDATION } from '@core/Constants/Validations';
import { useInput } from '@core/Hooks/UseInput';
import Button from '@gds/Button/Button';
import { SuccessTickCircle } from '@gds/Icons/Paths/SuccessTickCircle';
import { Input } from '@gds/Input/Input';
import { Typography } from '@gds/Typography/Typography';

import type { EmailInputFormProps } from './EmailInputForm.entity';

import styles from './EmailInputForm.module.css';

export const EmailInputForm = ({ strings, isSubscribed, onSubmit }: EmailInputFormProps) => {
  const {
    value: email,
    isValid,
    error,
    handleBlur,
    handleChange,
    setCustomError,
  } = useInput(EMAIL_VALIDATION, strings.emailInvalidError);
  const [isLoading, setIsLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!isValid) return;

    setIsLoading(true);
    const submitError = await onSubmit(email);
    if (submitError) setCustomError(submitError);
    setIsLoading(false);
  };

  if (isSubscribed) {
    return (
      <div className={styles.signUpSuccessMessage}>
        <SuccessTickCircle />
        <Typography variant="body1">{strings.signUpSucess}</Typography>
      </div>
    );
  }
  return (
    <form onSubmit={handleSubmit} className={styles.emailInput} id="email-input-form">
      <Input
        onChange={handleChange}
        value={email}
        onBlur={handleBlur}
        {...(((email && !isValid) || error) && { error })}
        name="email"
        disabled={isLoading}
        placeholder={strings.placeHolder}
        fullWidth
        type="email"
        required
      />
      <Typography Component="span" className={styles.tcs} variant="caption5">
        {strings.bySubmitting}{' '}
        <Link
          href="/privacy"
          target="_blank"
          rel="noopener noreferrer"
          aria-label="privacy policy link"
        >
          {strings.privacyPolicy}
        </Link>
      </Typography>
      <Button loading={isLoading}>{strings.signUpCta}</Button>
    </form>
  );
};
